import * as uuid from 'uuid';
import { isProduction } from 'js/sign-components/common/ts-utils';
import { AppActionLogEntry } from './index';

const log: AppActionLogEntry[] = [];

/**
 * This function walks the top 2 layers of appActions and wraps any functions
 * so that they can be logged to window.appActionLog. It was helpful in
 * converting Editor's namespace so I could see what actually is being called
 * and its responses.
 */
export const logAppActionsInDevelopment = (original: any, path: string[] = []) => {
  if (isProduction()) {
    return original;
  }

  const unwrapResult = async (result: any) => {
    try {
      return await result;
    } catch (e) {
      if (e instanceof Error) {
        return `Error: ${e.message}`;
      }
    }
  };

  const logResult = async (
    { name, args, result }: Pick<AppActionLogEntry, 'name' | 'args' | 'result'>,
  ) => {
    const detail: AppActionLogEntry = {
      id: uuid.v4(),
      timestamp: (new Date()).toISOString(),
      name,
      args,
      result: await unwrapResult(result),
    };


    log.push(detail);
    while (log.length > 20) {
      log.shift();
    }

    // Check if "dispatchEvent" is supported first.
    // Although this code should only run on non-prod
    // environments, for some automated tests this will
    // sometimes needlessly throw an error.
    if ('dispatchEvent' in document.body && typeof document.body.dispatchEvent === 'function') {
      document.body.dispatchEvent(
        new CustomEvent('app-action-log', { detail: [...log] }),
      );
    }
  };

  // Ideally we'd move these out of app actions, but for now I'm just going to
  // black list them so we don't log calls to local validation functions.
  const schemaObjects = [
    'dataSchema',
    'templateResponse',
    'templateErrorResponse',
  ];

  return Object.keys(original).reduce((actions, key) => {
    const value = actions[key];
    if (value == null
      || schemaObjects.includes(key)) {
      // skip it
    } else if (typeof value === 'function') {
      actions[key] = (...args: any) => {
        const result = value(...args);


        const name = path.concat([key]).join('.');
        logResult({
          name,
          args,
          result,
        });

        return result;
      };
    } else if (typeof value === 'object' && path.length < 1) {
      actions[key] = logAppActionsInDevelopment(
        actions[key],
        path.concat([key]),
      );
    }

    return actions;
  }, original);
};
